// CSV out. A front component runs in a sandbox that may have no document or Blob, so a
// real download is attempted only when both exist; otherwise the text is shown in a box
// to copy. Either way the user gets their file, and the screen says which happened.
import { useState } from 'react';

import { DeskButton } from 'src/front-components/theme/desk-controls';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE } from 'src/front-components/theme/shell-tokens';

export const tryDownload = (filename: string, text: string): boolean => {
  if (typeof document === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return false;
  }

  try {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    return true;
  } catch {
    return false;
  }
};

export const useCsvExport = (): {
  run: (filename: string, text: string) => void;
  panel: { filename: string; text: string } | null;
  close: () => void;
} => {
  const [panel, setPanel] = useState<{ filename: string; text: string } | null>(null);

  return {
    run: (filename, text) => setPanel(tryDownload(filename, text) ? null : { filename, text }),
    panel,
    close: () => setPanel(null),
  };
};

export const CsvPanel = ({
  scheme,
  panel,
  onClose,
}: {
  scheme: string;
  panel: { filename: string; text: string } | null;
  onClose: () => void;
}) => {
  const shell = getShellColors(scheme);
  const [copied, setCopied] = useState(false);

  if (panel === null) return null;

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(panel.text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div style={{ border: `1px solid ${shell.borderMedium}`, borderRadius: SHELL_RADIUS.md, padding: SHELL_SPACE[3], marginBottom: SHELL_SPACE[3], background: shell.backgroundSecondary }}>
      <div style={{ ...SHELL_TYPE.body, color: shell.textSecondary, marginBottom: SHELL_SPACE[2] }}>
        This page cannot save files directly. Copy the text below into a file called <strong>{panel.filename}</strong>.
      </div>
      <textarea
        readOnly
        value={panel.text}
        rows={8}
        style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: SHELL_TYPE.caption.fontSize, background: shell.background, color: shell.text, border: `1px solid ${shell.borderMedium}`, borderRadius: SHELL_RADIUS.sm }}
      />
      <div style={{ display: 'flex', gap: SHELL_SPACE[2], marginTop: SHELL_SPACE[2] }}>
        <DeskButton scheme={scheme} primary onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </DeskButton>
        <DeskButton scheme={scheme} onClick={onClose}>
          Close
        </DeskButton>
      </div>
    </div>
  );
};
